import { MdSearch } from "react-icons/md";
import { useEffect, useMemo, useState } from "react";
import GroomerModalComponent from "../GromerModalComponent";
import DataTable from "@/app/components/ui/Reusable/DataTable";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { getGroomerClinicColumns } from "@/app/hooks/custom_hooks";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { useClinicListAPI } from "@/app/hooks/api_hooks/org/useClinicListAPI";
import { useConfigurationGroomerStaffListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationGroomerStaffListAPI";

type ClinicItem = {
  groomer_ids?: string[];
  active_groomer_count?: string;
  clinic_id: string;
  clinic_name: string;
  city: string;
};

const ClinicGroomerMappingList = () => {
  const { NO_APPOINTMENTS } = useImageConstants();
  const { execute: fetchClinics, clinicList } = useClinicListAPI();
  const { execute: fetchGroomers } = useConfigurationGroomerStaffListAPI();
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClinic, setSelectedClinic] = useState<any | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const openModal = (clinic: ClinicItem) => {
    setSelectedClinic(clinic);
    setModalOpen(true);
  };
  const columns = getGroomerClinicColumns(openModal);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    Promise.all([fetchClinics()]).then(() => {
      if (!active) return;
      setIsLoading(false);
    });
    return () => {
      active = false;
    };
  }, [fetchClinics, fetchGroomers]);

  const filteredClinics = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return clinicList;
    return clinicList.filter(
      (c) =>
        c.clinic_name?.toLowerCase().includes(q) ||
        c.city?.toLowerCase().includes(q) ||
        c.clinic_id?.toLowerCase().includes(q),
    );
  }, [clinicList, searchQuery]);

  return (
    <div className="px-5 py-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Clinic List</h2>
          <p className="mt-1 text-sm text-slate-500">Map groomers to clinics</p>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex w-full flex-col gap-2 sm:max-w-xl">
          <div className="flex items-center gap-2">
            <div className="relative w-full">
              <MdSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    // state already updates filter
                  }
                }}
                placeholder="Search clinic name, city, or ID"
                className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-10 text-sm text-slate-900 shadow-sm focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setSearchQuery("")}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        {isLoading ? (
          <>
            <SkeletonType1 />
            <SkeletonType1 className="mt-4" />
          </>
        ) : filteredClinics.length === 0 ? (
          <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Clinic Data" description="No clinics found." />
        ) : (
          <div>
            <DataTable data={filteredClinics || []} columns={columns} maxHeight="600px" />
          </div>
          // <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white">
          //   <table className="min-w-full divide-y divide-slate-100">
          //     <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
          //       <tr>
          //         <th className="px-4 py-3">Clinic</th>
          //         <th className="px-4 py-3 text-center">Groomers</th>
          //         <th className="px-4 py-3 text-right">Actions</th>
          //       </tr>
          //     </thead>
          //     <tbody className="divide-y divide-slate-100 text-xs">
          //       {filteredClinics.map((clinic) => (
          //         <tr key={clinic.clinic_id} className="hover:bg-slate-50/70">
          //           <td className="px-4 py-3 font-medium text-slate-900">{clinic.clinic_name || "-"}</td>
          //           <td className="px-4 py-3 text-slate-600 text-center">{clinic?.active_groomer_count || "-"}</td>
          //           <td className="px-4 py-3 text-right">
          //             <button
          //               type="button"
          //               onClick={() => openModal(clinic)}
          //               className="inline-flex h-9 items-center justify-center gap-2 rounded-xl bg-slate-100 px-3 text-slate-700 transition hover:bg-slate-200"
          //             >
          //               <BiEdit className="h-4 w-4" />
          //               Map Groomer
          //             </button>
          //           </td>
          //         </tr>
          //       ))}
          //     </tbody>
          //   </table>
          // </div>
        )}
      </div>

      {modalOpen && (
        <GroomerModalComponent
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          clinic={selectedClinic}
          onSubmitSuccess={() => fetchClinics()}
        />
      )}
    </div>
  );
};

export default ClinicGroomerMappingList;
